<template>
  <div>
    <page-header title="舆情概览" description="汇总各平台公开内容与评论，及时发现并处理舆情风险">
      <el-button :loading="starting" :disabled="loading || !readyPlatforms.length" type="primary" @click="start">
        {{ starting ? '启动中' : '发起验证采集' }}
      </el-button>
    </page-header>
    <el-alert v-if="loadError" class="mb" type="warning" :title="loadError" :closable="false" show-icon/>

    <div v-loading="loading" element-loading-text="正在加载舆情概览…">
      <el-tabs v-model="activeTab" class="overview-tabs">
        <el-tab-pane label="监测概览" name="overview">
      <el-row :gutter="16">
        <el-col v-for="stat in stats" :key="stat.label" :md="6">
          <el-card><div>{{ stat.label }}</div><h2>{{ stat.value }}</h2><small>{{ stat.hint }}</small></el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16" class="mt">
        <el-col :md="14">
          <el-card><div slot="header">需要关注</div><risk-table :items="pendingRisks.slice(0, 5)" @open="$router.push('/yuqing/risk')"/></el-card>
        </el-col>
        <el-col :md="10">
          <el-card>
            <div slot="header">平台采集状态</div>
            <p><b>{{ run && run.status === 'running' ? '正在采集最新舆情' : '等待开始采集' }}</b></p>
            <p class="muted">{{ run ? (run.stop_reason || run.stage) : '请先配置品牌词，并完成平台验证' }}</p>
            <el-tag v-for="platform in readiness" :key="platform.platform" :type="platform.search_ready ? 'success' : 'warning'">
              {{ platform.display_name }} · {{ platform.search_ready ? '可采集' : '待验证' }}
            </el-tag>
          </el-card>
        </el-col>
      </el-row>

      <el-card class="mt">
        <div slot="header">最新发现内容</div>
        <el-table :data="contents.slice(0, 10)" empty-text="暂无公开舆情内容">
          <el-table-column prop="title" label="内容" min-width="260"/>
          <el-table-column prop="platform" label="平台" width="100"/>
          <el-table-column label="发布时间" width="170"><template slot-scope="scope">{{ formatTime(scope.row.published_at) }}</template></el-table-column>
          <el-table-column label="原文" width="80"><template slot-scope="scope"><a v-if="scope.row.source_url" :href="scope.row.source_url" target="_blank">查看</a></template></el-table-column>
        </el-table>
      </el-card>
        </el-tab-pane>

        <el-tab-pane label="运行时间轴" name="timeline">
      <el-alert v-if="daily" class="timeline-alert" :type="conclusionType(daily.conclusion)" :title="daily.conclusion_message" :closable="false" show-icon/>
      <el-card class="monitor-card">
        <div slot="header" class="card-header">
          <div><b>{{ daily ? daily.date : today() }} 今日舆情监测</b><div class="header-hint">点击已执行的运行查看独立时间轴</div></div>
          <el-button type="text" :loading="timelineLoading" @click="load">刷新</el-button>
        </div>
        <el-empty v-if="!monitorRows.length" description="今日尚无计划或运行记录"/>
        <div v-for="row in monitorRows" :key="row.key" class="monitor-run">
          <div :class="['run-summary', { clickable: !!row.run_id }]" @click="toggleRun(row)">
            <div class="run-time">{{ timeOnly(row.time) }}</div>
            <div class="run-trigger">{{ triggerLabel(row.trigger_type) }}</div>
            <el-tag size="mini" :type="runStatusType(row.status)">{{ runStatusLabel(row.status) }}</el-tag>
            <div class="run-message">{{ row.message }}</div>
            <i v-if="row.run_id" :class="activeRunId === row.run_id ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"/>
          </div>
          <div v-if="activeRunId === row.run_id" class="timeline-panel" v-loading="timelineLoading">
            <el-alert v-if="timelineError" type="warning" :title="timelineError" :closable="false" show-icon/>
            <el-timeline v-else-if="timeline && timeline.events && timeline.events.length">
              <el-timeline-item v-for="(event, index) in timeline.events" :key="`${row.run_id}-${index}`" :timestamp="formatTime(event.time)" :type="eventType(event.level)">
                <div class="event-title">{{ event.title }}</div>
                <div v-if="event.description" class="event-description">{{ event.description }}</div>
                <el-button v-if="event.diagnostic" class="diagnostic-button" type="text" size="mini" @click.stop="showDiagnostic(event)">查看诊断详情</el-button>
              </el-timeline-item>
            </el-timeline>
            <el-empty v-else description="暂无运行事件"/>
          </div>
        </div>
      </el-card>
        </el-tab-pane>
      </el-tabs>
    </div>

    <el-dialog title="采集诊断详情" :visible.sync="diagnosticVisible" width="640px">
      <pre class="diagnostic-content">{{ diagnosticText }}</pre>
      <span slot="footer"><el-button @click="copyDiagnostic">复制详情</el-button><el-button type="primary" @click="diagnosticVisible = false">关闭</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi, loadLatestDashboard } from '@/api/yuqing'
import PageHeader from '../components/PageHeader.vue'
import RiskTable from '../components/RiskTable.vue'

export default {
  components: { PageHeader, RiskTable },
  data () {
    return {
      activeTab: 'overview', risks: [], readiness: [], run: null, daily: null, contents: [], comments: [],
      loading: false, starting: false, loadError: '', refreshTimer: null,
      activeRunId: '', timeline: null, timelineLoading: false, timelineError: '',
      timelineCache: {}, autoExpanded: false, diagnosticVisible: false, diagnosticText: ''
    }
  },
  computed: {
    pendingRisks () { return this.risks.filter(item => !['已关闭', '误报', '已归档'].includes(item.process_status)) },
    readyPlatforms () { return this.readiness.filter(item => item.search_ready) },
    stats () {
      return [
        { label: '本轮采集内容', value: this.contents.length, hint: '已自动去除重复内容' },
        { label: '本轮新增评论', value: this.comments.filter(item => item.is_new).length, hint: '仅统计本轮新发现的评论' },
        { label: '待处理风险', value: this.pendingRisks.length, hint: '请及时确认并处理' },
        { label: '可采集平台', value: this.readyPlatforms.length, hint: '完成平台验证后即可采集' }
      ]
    },
    monitorRows () {
      if (!this.daily) return []
      const runs = this.daily.runs || []
      const byId = {}
      runs.forEach(item => { byId[item.run_id] = item })
      const rows = (this.daily.schedule_checks || []).map(item => {
        const run = item.run_id && byId[item.run_id]
        return {
          key: `schedule-${item.scheduled_at}`,
          run_id: item.run_id,
          time: item.scheduled_at,
          trigger_type: 'scheduled',
          status: item.status,
          message: run ? run.conclusion_message : item.message
        }
      })
      runs.filter(item => item.trigger_type !== 'scheduled').forEach(item => {
        rows.push({
          key: item.run_id,
          run_id: item.run_id,
          time: item.created_at,
          trigger_type: item.trigger_type,
          status: item.status,
          message: item.conclusion_message
        })
      })
      return rows.sort((left, right) => new Date(left.time).getTime() - new Date(right.time).getTime())
    }
  },
  mounted () { this.load() },
  beforeDestroy () { this.clearRefresh() },
  methods: {
    today () {
      const date = new Date()
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },
    async load () {
      this.loading = true
      this.loadError = ''
      try {
        const results = await Promise.allSettled([
          yuqingApi.get('risk-records'),
          yuqingApi.get('platform-readiness'),
          loadLatestDashboard(),
          yuqingApi.get('runs/daily', { date: this.today() })
        ])
        if (results[0].status === 'fulfilled') this.risks = results[0].value.risks || []
        if (results[1].status === 'fulfilled') this.readiness = results[1].value.readiness || []
        if (results[2].status === 'fulfilled') {
          this.run = results[2].value.run
          this.contents = results[2].value.collected_items || []
          this.comments = results[2].value.collected_comments || []
        }
        if (results[3].status === 'fulfilled') this.daily = results[3].value
        const failed = results.find(item => item.status === 'rejected')
        if (failed) this.loadError = failed.reason.message || '部分舆情数据暂不可用'
        const activeRun = this.daily && (this.daily.runs || []).find(item => item.run_id === this.activeRunId)
        if (activeRun) {
          this.$delete(this.timelineCache, this.activeRunId)
          await this.loadTimeline(this.activeRunId)
        } else {
          await this.expandLatestProblem()
        }
        this.scheduleRefresh()
      } finally {
        this.loading = false
      }
    },
    async start () {
      if (this.starting || !this.readyPlatforms.length) return
      const names = this.readyPlatforms.map(item => item.display_name).join('、')
      try {
        await this.$confirm(`将验证采集 ${names}，会产生 TikHub 和 AI 调用。无风险正文不会保存，是否继续？`, '发起验证采集', { type: 'warning', confirmButtonText: '开始采集' })
      } catch (error) { return }
      this.starting = true
      try {
        const result = await yuqingApi.post('public-opinion/refresh', {})
        if (result && result.status === 'not_executed') this.$message.warning(result.stop_reason || '本次采集未执行')
        else this.$message.success('验证采集已启动，可在今日舆情监测中查看进度')
        this.activeTab = 'timeline'
        this.autoExpanded = true
        await this.load()
        if (result && result.id) {
          this.$delete(this.timelineCache, result.id)
          await this.loadTimeline(result.id)
        }
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.starting = false
      }
    },
    async toggleRun (row) {
      if (!row.run_id) return
      if (this.activeRunId === row.run_id) {
        this.activeRunId = ''
        this.timeline = null
        return
      }
      await this.loadTimeline(row.run_id)
    },
    async loadTimeline (runId) {
      this.activeRunId = runId
      this.timelineError = ''
      if (this.timelineCache[runId]) {
        this.timeline = this.timelineCache[runId]
        return
      }
      this.timeline = null
      this.timelineLoading = true
      try {
        const result = await yuqingApi.get(`runs/${encodeURIComponent(runId)}/timeline`)
        this.$set(this.timelineCache, runId, result)
        this.timeline = result
      } catch (error) {
        this.timelineError = error.message || '运行时间轴暂不可用'
      } finally {
        this.timelineLoading = false
      }
    },
    async expandLatestProblem () {
      if (this.autoExpanded) return
      const problems = (this.daily && this.daily.runs || []).filter(item => ['risk_found', 'incomplete', 'not_executed', 'running'].includes(item.conclusion))
      if (!problems.length) return
      this.autoExpanded = true
      await this.loadTimeline(problems[0].run_id)
    },
    scheduleRefresh () {
      this.clearRefresh()
      if (this.daily && this.daily.runs && this.daily.runs.some(item => item.status === 'running')) {
        this.refreshTimer = setTimeout(() => this.load(), 5000)
      }
    },
    clearRefresh () {
      if (this.refreshTimer) clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    },
    showDiagnostic (event) {
      this.diagnosticText = JSON.stringify(event.diagnostic, null, 2)
      this.diagnosticVisible = true
    },
    async copyDiagnostic () {
      try {
        await navigator.clipboard.writeText(this.diagnosticText)
        this.$message.success('诊断详情已复制')
      } catch (error) {
        this.$message.warning('复制失败，请手动选择文本复制')
      }
    },
    conclusionType (value) { return ({ risk_found: 'error', no_risk_found: 'success', running: 'info', incomplete: 'warning', not_executed: 'warning', schedule_missing: 'error' })[value] || 'info' },
    triggerLabel (value) { return ({ manual: '手动验证', scheduled: '定时采集', retry: '补偿采集' })[value] || '采集运行' },
    runStatusLabel (status) { return ({ pending: '待执行', completed: '已完成', partial_completed: '部分完成', running: '运行中', missing: '未触发', disabled: '未启用', not_executed: '未执行', partial_failed: '部分完成', failed: '失败', budget_exhausted: '额度用完', stopped: '已停止' })[status] || status || '-' },
    runStatusType (status) { return ({ completed: 'success', running: 'primary', pending: 'info', partial_completed: 'warning', partial_failed: 'warning', missing: 'danger', disabled: 'danger', failed: 'danger', budget_exhausted: 'danger', not_executed: 'info', stopped: 'info' })[status] || 'info' },
    eventType (level) { return ({ success: 'success', warning: 'warning', error: 'danger', info: 'primary' })[level] || 'info' },
    timeOnly (value) { const formatted = this.formatTime(value); return formatted === '-' ? '-' : formatted.slice(11, 16) },
    formatTime (value) {
      if (value === null || value === undefined || value === '') return '-'
      const text = String(value)
      if (!/^\d{10,13}$/.test(text)) return text.replace('T', ' ').slice(0, 19)
      const timestamp = Number(text) * (text.length === 10 ? 1000 : 1)
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return '-'
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  }
}
</script>

<style scoped>
.mt { margin-top: 16px; }
.mb { margin-bottom: 16px; }
.timeline-alert { margin-bottom: 16px; }
.overview-tabs { padding: 0 4px; }
.muted, .header-hint { color: #909399; }
.header-hint { margin-top: 4px; font-size: 12px; font-weight: normal; }
.el-tag { margin: 2px; }
.card-header { display: flex; align-items: center; justify-content: space-between; }
.monitor-card >>> .el-card__body { padding: 0 20px; }
.monitor-run + .monitor-run { border-top: 1px solid #ebeef5; }
.run-summary { display: grid; grid-template-columns: 64px 88px 90px minmax(0, 1fr) 20px; align-items: center; gap: 12px; min-height: 58px; }
.run-summary.clickable { cursor: pointer; }
.run-summary.clickable:hover { background: #f8fafc; }
.run-time { font-weight: 600; font-variant-numeric: tabular-nums; }
.run-trigger { color: #303133; }
.run-message { overflow: hidden; color: #606266; text-overflow: ellipsis; white-space: nowrap; }
.timeline-panel { padding: 18px 18px 2px 152px; background: #fafafa; border-top: 1px solid #ebeef5; }
.event-title { color: #303133; font-weight: 500; }
.event-description { margin-top: 4px; color: #606266; line-height: 1.6; }
.diagnostic-button { padding: 4px 0 0; }
.diagnostic-content { max-height: 420px; margin: 0; padding: 14px; overflow: auto; color: #303133; background: #f5f7fa; border-radius: 4px; white-space: pre-wrap; word-break: break-all; }
</style>
